import {
    TenantOnboardStep,
    TenantOnboardSubstep,
    TenantCreationSubstep,
    BusinessCreationSubstep,
    SubscriptionSubstep,
    VendorDetailsSubstep,
    PosSetupSubstep,
    TenantNotesSubstep,
    OnboardingProcessSubstep,
} from './tenantOnboardEnums'

export interface SubstepDefinition {
    substep: TenantOnboardSubstep
    isBusinessStep: boolean
    stepName: string

    excludeFromProgress?: boolean
    interactive?: boolean
}

export interface StageDefinition {
    stage: TenantOnboardStep
    stageName: string
    substeps: SubstepDefinition[]
}

export const ONBOARDING_FLOW: StageDefinition[] = [
    {
        stage: TenantOnboardStep.TENANT_CREATION,
        stageName: 'Tenant Details',
        substeps: [
            {
                substep: TenantCreationSubstep.CREATE_TENANT,
                isBusinessStep: false,
                stepName: 'Create Tenant',
            },
            {
                substep: TenantCreationSubstep.CREATE_NOTIFICATION_CONTEXT,
                isBusinessStep: false,
                stepName: 'Create Notification Context',
            },
            {
                substep: TenantCreationSubstep.CREATE_ADMIN_USER,
                isBusinessStep: false,
                stepName: 'Create Admin User',
            },
            {
                substep: TenantCreationSubstep.CREATE_ES_INDEX,
                isBusinessStep: false,
                stepName: 'Create ES Index',
            },
            {
                substep: TenantCreationSubstep.CREATE_REDMINE_PROJECT,
                isBusinessStep: false,
                stepName: 'Create Redmine Project',
                excludeFromProgress: true,
            },
        ],
    },
    {
        stage: TenantOnboardStep.BUSINESS_CREATION,
        stageName: 'Manage Business',
        substeps: [
            {
                substep: BusinessCreationSubstep.UPSERT_BUSINESS,
                isBusinessStep: true,
                stepName: 'Upsert Business',
            },
            {
                substep: BusinessCreationSubstep.SETUP_FIELD_MAPPING,
                isBusinessStep: true,
                stepName: 'Setup Field Mapping',
            },
        ],
    },
    {
        stage: TenantOnboardStep.SUBSCRIPTION,
        stageName: 'Subscription',
        substeps: [
            {
                substep: SubscriptionSubstep.SAVE_SUBSCRIPTION,
                isBusinessStep: true,
                stepName: 'Save Subscription',
            },
            {
                substep: SubscriptionSubstep.CREATE_ONE_TIME_PACK_INVOICE,
                isBusinessStep: true,
                stepName: 'Create One-Time Pack Invoice',
            },
            {
                substep: SubscriptionSubstep.UPDATE_PERMISSIONS,
                isBusinessStep: true,
                stepName: 'Update Permissions',
            },
            {
                substep: SubscriptionSubstep.APPLY_BUSINESS_CONFIG,
                isBusinessStep: true,
                stepName: 'Apply Business Config',
            },
            {
                substep: SubscriptionSubstep.SETUP_TICKETDESK_MODULE,
                isBusinessStep: true,
                stepName: 'Setup TicketDesk Module',
            },
            {
                substep: SubscriptionSubstep.SETUP_FILE_IMPORT,
                isBusinessStep: true,
                stepName: 'Setup File Import',
            },
           
            {
                substep: SubscriptionSubstep.SETUP_LEAD_MANAGEMENT,
                isBusinessStep: true,
                stepName: 'Setup Lead Management',
            },
            {
                substep: SubscriptionSubstep.SETUP_COMMERCE_CONNECT,
                isBusinessStep: true,
                stepName: 'Setup Commerce Connect',
            },
            {
                substep: SubscriptionSubstep.SETUP_FLIPSELL,
                isBusinessStep: true,
                stepName: 'Setup Flipsell',
            },
             {
                substep: SubscriptionSubstep.SETUP_REQUEST_HANDLERS,
                isBusinessStep: false,
                stepName: 'Setup Request Handlers',
            },
            {
                substep: SubscriptionSubstep.ADD_ONBOARDING_USERS,
                isBusinessStep: true,
                stepName: 'Add Onboarding Users',
                excludeFromProgress: true,
                interactive: true,
            },
            {
                substep: SubscriptionSubstep.ASSIGN_MODULE_OWNERS,
                isBusinessStep: true,
                stepName: 'Assign Module Owners',
                excludeFromProgress: true,
                interactive: true,
            },
            {
                substep: SubscriptionSubstep.SETUP_SUPERSET_MODULE,
                isBusinessStep: false,
                stepName: 'Setup Superset Module',
            },
            // Last in the stage: raises tickets for the modules that ended up
            // subscribed, so it must run after every provisioning substep.
            {
                substep: SubscriptionSubstep.REDMINE_TASK_CREATION,
                isBusinessStep: true,
                stepName: 'Redmine Task Creation',
                excludeFromProgress: true,
            },{

                substep: SubscriptionSubstep.OIR_FORM_REDMINE_TICKET_CREATION,
                isBusinessStep: true,
                stepName: 'OIR Form Redmine Ticket Creation',
                excludeFromProgress: true,
            },

        ],
    },
    {
        stage: TenantOnboardStep.VENDOR_DETAILS,
        stageName: 'Vendor Details',
        substeps: [
            {
                substep: VendorDetailsSubstep.SAVE_PROVIDER_CONFIG,
                isBusinessStep: true,
                stepName: 'Save Provider Config',
            },
        ],
    },
    {
        stage: TenantOnboardStep.POS_SETUP,
        stageName: 'POS Details',
        substeps: [
            {
                substep: PosSetupSubstep.SAVE_POS_DETAILS,
                isBusinessStep: true,
                stepName: 'Save POS Details',
            },
            {
                substep: PosSetupSubstep.VERIFY_BILL_SYNC,
                isBusinessStep: true,
                stepName: 'Verify Bill Sync',
            },
        ],
    },
    {
        stage: TenantOnboardStep.TENANT_NOTES,
        stageName: 'Tenant Notes',
        substeps: [
            {
                substep: TenantNotesSubstep.SAVE_NOTES,
                isBusinessStep: false,
                stepName: 'Save Notes',
            },
        ],
    },
]

// Stages that run in the creation pipeline but are excluded from
// ONBOARDING_FLOW so they never count toward setup progress on the
// status/landing endpoints (mirrors the backend AUXILIARY_STAGES).
export const AUXILIARY_STAGES: StageDefinition[] = [
    {
        stage: TenantOnboardStep.ONBOARDING_PROCESS,
        stageName: 'Onboarding Process',
        substeps: [
            {
                substep: OnboardingProcessSubstep.SAVE_ONBOARDING_USERS,
                isBusinessStep: false,
                stepName: 'Save Onboarding Users',
            },
            {
                substep: OnboardingProcessSubstep.CREATE_DASHBOARD_ACCOUNTS,
                isBusinessStep: false,
                stepName: 'Create Dashboard Accounts',
            },
            {
                substep: OnboardingProcessSubstep.SEND_WELCOME_EMAILS,
                isBusinessStep: false,
                stepName: 'Send Welcome Emails',
            },
            {
                substep: OnboardingProcessSubstep.CREATE_DISCORD_POST,
                isBusinessStep: false,
                stepName: 'Discord Forum Post',
            },
        ],
    },
]

const ALL_STAGES: StageDefinition[] = [...ONBOARDING_FLOW, ...AUXILIARY_STAGES]

export const isBusinessLevelStep = (
    stage: TenantOnboardStep,
    substep: TenantOnboardSubstep
): boolean => {
    const stageFlow = ALL_STAGES.find((s) => s.stage === stage)
    if (!stageFlow) return false
    const substepDef = stageFlow.substeps.find((s) => s.substep === substep)
    return substepDef?.isBusinessStep ?? false
}

export const getStepNames = (
    stage: TenantOnboardStep,
    substep: TenantOnboardSubstep
): { stageName: string; stepName: string } => {
    const stageFlow = ALL_STAGES.find((s) => s.stage === stage)
    if (!stageFlow) return { stageName: stage, stepName: substep }
    const substepDef = stageFlow.substeps.find((s) => s.substep === substep)
    return {
        stageName: stageFlow.stageName,
        stepName: substepDef?.stepName ?? substep,
    }
}
